import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { FONT, SERIF, colors, radii, shadows } from '../theme';
import { Diamond, ScanFace } from 'lucide-react';

export default function LoginPage() {
  const { login, loginWithToken } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [nativeHasToken, setNativeHasToken] = useState(false);
  const [nativeSavedUsername, setNativeSavedUsername] = useState('');
  const isNative = typeof window !== 'undefined' && !!window.VoxioxNative;

  // Check biometric availability
  useEffect(() => {
    if (isNative) {
      // Native app — check if Keychain has saved token
      setBiometricAvailable(true);
      window.VoxioxBridge = window.VoxioxBridge || {};
      window.VoxioxBridge.onAuthTokenCheck = (hasToken, savedUsername) => {
        setNativeHasToken(hasToken);
        if (savedUsername) setNativeSavedUsername(savedUsername);
      };
      window.VoxioxNative.hasAuthToken();
    } else if (window.PublicKeyCredential) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setBiometricAvailable)
        .catch(() => {});
    }
    return () => {
      if (window.VoxioxBridge) delete window.VoxioxBridge.onAuthTokenCheck;
    };
  }, [isNative]);

  // Check WebAuthn passkeys when username changes (non-native only)
  const checkPasskeys = useCallback(async (name) => {
    if (!name || !biometricAvailable || isNative) return;
    try {
      const { hasKeys } = await api.webauthnHasKeys(name);
      setHasPasskeys(hasKeys);
    } catch (e) {
      setHasPasskeys(false);
    }
  }, [biometricAvailable, isNative]);

  useEffect(() => {
    const timer = setTimeout(() => checkPasskeys(username), 500);
    return () => clearTimeout(timer);
  }, [username, checkPasskeys]);

  // Standard login — also saves token to Keychain for native apps
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(username, password);
      // Save token to native Keychain for future Face ID login
      if (isNative && window.VoxioxNative.saveAuthToken && result?.token) {
        window.VoxioxNative.saveAuthToken(result.token, username);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    setError('');
    setLoading(true);
    try {
      if (isNative && window.VoxioxNative.authenticateWithBiometrics) {
        // Native Face ID → Keychain token
        const result = await new Promise((resolve, reject) => {
          window.VoxioxBridge = window.VoxioxBridge || {};
          window.VoxioxBridge.onBiometricResult = (success, token, savedUsername, errorMsg) => {
            if (success && token) resolve({ token, username: savedUsername });
            else reject(new Error(errorMsg || 'Биометрия отклонена'));
          };
          window.VoxioxNative.authenticateWithBiometrics(username || nativeSavedUsername || '');
        });
        // Verify token is still valid by calling /api/auth/me
        try {
          localStorage.setItem('voxiox_token', result.token);
          const userData = await api.me();
          loginWithToken(result.token, userData);
        } catch {
          // Token expired — need to login with password again
          localStorage.removeItem('voxiox_token');
          KeychainClear();
          setError('Сессия истекла. Войдите с паролем.');
        }
      } else {
        // Standard WebAuthn flow
        const { startAuthentication } = await import('@simplewebauthn/browser');
        const options = await api.webauthnLoginOptions(username || undefined);
        const credential = await startAuthentication(options);
        credential.username = username || undefined;
        const data = await api.webauthnLogin(credential);
        loginWithToken(data.token, data.user);
      }
    } catch (err) {
      setError(err.message || 'Не удалось войти через Face ID');
    } finally {
      setLoading(false);
    }
  };

  const KeychainClear = () => {
    if (isNative && window.VoxioxNative.clearAuthToken) {
      window.VoxioxNative.clearAuthToken();
    }
    setNativeHasToken(false);
  };

  // Show Face ID button if: native app with saved token, OR WebAuthn with passkeys
  const showBiometric = (isNative && biometricAvailable && nativeHasToken) || (!isNative && biometricAvailable && hasPasskeys);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <Diamond size={28} color={colors.gray900} strokeWidth={2} />
          <span style={styles.logoText}>VOXIOX</span>
        </div>

        <form onSubmit={submit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Имя пользователя</label>
            <input
              style={styles.input}
              value={username || nativeSavedUsername}
              onChange={e => { setUsername(e.target.value); setNativeSavedUsername(''); }}
              placeholder="username"
              autoComplete="username"
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Пароль</label>
            <input
              type="password"
              style={styles.input}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" disabled={loading} style={{
            ...styles.btn,
            ...(loading ? styles.btnDisabled : {}),
          }}>
            {loading ? 'Подключение...' : 'Войти'}
          </button>
        </form>

        {showBiometric && (
          <>
            <div style={styles.divider}>
              <span style={styles.dividerLine} />
              <span style={styles.dividerText}>или</span>
              <span style={styles.dividerLine} />
            </div>
            <button onClick={handleBiometricLogin} disabled={loading} style={styles.biometricBtn}>
              <ScanFace size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              {isNative ? `Войти с Face ID${nativeSavedUsername ? ` (${nativeSavedUsername})` : ''}` : 'Войти с Face ID'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT,
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    background: colors.white,
    borderRadius: radii.lg,
    padding: '40px 32px',
    boxShadow: shadows.sm,
    border: `1px solid ${colors.gray100}`,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 32,
  },
  logoText: { fontSize: 26, fontWeight: 600, color: colors.gray900, letterSpacing: 0.5, fontFamily: SERIF },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: colors.gray600 },
  input: {
    background: colors.gray50, border: `1px solid ${colors.gray200}`,
    borderRadius: radii.md, padding: '12px 14px',
    color: colors.gray900, fontSize: 15, fontFamily: 'inherit',
    outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  error: {
    background: colors.redLight, border: '1px solid rgba(221,122,92,0.35)',
    color: colors.red, fontSize: 13, padding: '10px 14px', borderRadius: radii.sm,
  },
  btn: {
    background: colors.gray900, border: 'none', color: colors.white,
    padding: '16px', fontSize: 16, fontWeight: 600,
    cursor: 'pointer', borderRadius: radii.lg, fontFamily: 'inherit',
    transition: 'transform 0.15s, opacity 0.15s', marginTop: 4,
  },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  divider: {
    display: 'flex', alignItems: 'center', gap: 12,
    margin: '24px 0 16px',
  },
  dividerLine: { flex: 1, height: 1, background: colors.gray200 },
  dividerText: { fontSize: 13, color: colors.gray400 },
  biometricBtn: {
    width: '100%', background: colors.gray50, border: `1px solid ${colors.gray200}`,
    color: colors.gray900, padding: '16px', fontSize: 15, fontWeight: 500,
    cursor: 'pointer', borderRadius: radii.lg, fontFamily: 'inherit',
    transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};
